<template>

  <v-row>
    <v-col cols="12">
      <v-table-toolbar>
        <v-spacer/>
        <v-btn
          class="mr-4 bg-primary"
          color="white"
          prepend-icon="far fa-plus"
          @click="showCouponDialog = true"
        >
          {{ $t('buttons.create') }}
        </v-btn>
      </v-table-toolbar>

    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <table-filter-card @reset-filters="resetFilter" :active-filter-count="activeFilterCount">
        <table-filter-card-item title="Search">
          <search-text-field
            :label="$t('components.CouponTablePage.search')"
            :disabled="store.networkBusy"
            data-pw="couponsSearchBox"
            @update:text="text => columns.title.filter = text"
          />
        </table-filter-card-item>
        <table-filter-card-item :title="columns.title.title">
          <filter-text-field
            :label="columns.title.title"
            v-model="titleFilter"
            data-pw="filterListTitle"
          />
        </table-filter-card-item>
        <table-filter-card-item :title="columns.type.title">
          <v-select
            v-model="columns.type.filter"
            :items="couponTypes"
            :label="$t('coupon.type')"
            density="compact"
            variant="outlined"
            clearable
            data-pw="couponTypeFilter"
          />
        </table-filter-card-item>
        <table-filter-card-item :title="columns.enabled.title">
          <v-radio-group v-model="columns.enabled.filter" density="compact">
            <v-radio
              :label="$t('common.yes')"
              value="true"
              data-pw="filterEnabledYes"
            />
            <v-radio
              :label="$t('common.no')"
              value="false"
              data-pw="filterEnabledNo"
            />
            <v-radio
              :label="$t('common.all')"
              value="all"
              data-pw="filterEnabledAll"
            />
          </v-radio-group>
        </table-filter-card-item>
        <table-filter-card-item :title="$t('coupon.used')">
          <v-radio-group v-model="columns.usageCount.filter" density="compact">
            <v-radio
              :label="$t('common.yes')"
              value="true"
              data-pw="filterUsedYes"
            />
            <v-radio
              :label="$t('common.no')"
              value="false"
              data-pw="filterUsedNo"
            />
            <v-radio
              :label="$t('common.all')"
              value="all"
              data-pw="filterUsedAll"
            />
          </v-radio-group>
        </table-filter-card-item>
      </table-filter-card>
    </v-col>
    <v-col md="9" lg="10">
      <v-data-table-server
        density="compact"
        :loading="store.networkBusy"
        :headers="headers"
        :items="coupons"
        :items-per-page="perPage"
        :items-length="0"
        data-pw="couponsTable"
        @click:row="onClickRow"
        @update:sort-by="onSort"
        :sort-by="sort"
        :hover="true"
        hide-default-footer
      >
        <template v-slot:no-data>
          <h5 class="pa-2 d-flex align-center justify-center align-items-center">
            {{ $t("common.no_data_message") }}
          </h5>
        </template>

        <template v-slot:loader>
          <v-sheet
            class="d-flex align-center justify-center"
            height="100%"
          >
            <v-progress-linear
              indeterminate
              color="primary"
            ></v-progress-linear>
          </v-sheet>
        </template>

        <template v-slot:item.type="{ item }">
          {{ $t(`couponTypes.${item["type"]}`) }}
        </template>

        <template v-slot:item.enabled="{ item }">
          {{ item.enabled ? t("yes") : t("no") }}
        </template>

        <template v-slot:item.expiresAt="{ item }">
          {{ toLocaleDateTime(item["expiresAt"]) }}
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ toLocaleDateTime(item["createdAt"]) }}
        </template>

        <template v-slot:bottom>
          <v-divider/>
          <pagination-controls
            v-if="paginatedData"
            class="text-center pt-2 v-table__pagination-controls"
            :pagination-footer-data="paginationFooterData"
            @update:per-page="onUpdatePerPage"
            @on-next="onNext"
            @on-prev="onPrev"
            @on-page-change="onPageChange"
            data-pw="couponCodePaginationControls"
          />
        </template>
      </v-data-table-server>

    </v-col>
  </v-row>

  <v-dialog v-model="showCouponDialog" persistent max-width="800">
    <create-coupon-dialog
      @close="showCouponDialog = false; fetchPage()"
    />
  </v-dialog>
</template>

<script setup lang="ts">
import {computed, onMounted, Ref, ref, watch} from "vue";
import { Coupon, isYapiError, Paginated, YapiError} from "@YenzaCT/sdk";
import {useRouter, useRoute} from "vue-router";
import {toLocaleDateTime} from "@/lib/time";
import {useI18n} from "vue-i18n";

import CreateCouponDialog from "@/pages/Manage/CouponTablePage/components/CreateCouponDialog.vue";
import yapi from "@/lib/yapi";
import PaginationControls from "@/components/PaginationControls.vue";
import {ColumnsConfig, FooterData, footerDataFactory, usePagination} from "@/lib/pagination";
import {useGlobalStore} from "@/store";
import SearchTextField from "@/components/SearchTextField.vue";
import FilterTextField from "@/components/DataTable/FilterTextField.vue";
import {VSelectOption} from "@/lib/vuetify";
import {CouponPaginationParams} from "@YenzaCT/sdk";
import {WritableComputedRef} from "@vue/reactivity";
import TableFilterCardItem from "@/components/TableFilterCardItem.vue";
import TableFilterCard from "@/components/TableFilterCard.vue";

const router = useRouter();
const route = useRoute();
const store = useGlobalStore();
const {t} = useI18n();

const paginatedData: Ref<Paginated<Coupon> | undefined> = ref();
let paginationFooterData: FooterData = footerDataFactory();

const coupons = ref<Coupon[]>([]);
const showCouponDialog: Ref<boolean> = ref(false);

const used = ref("all");

const cols: ColumnsConfig = {
  id: {
    title: t("coupon.id"),
    modelKey: "_id",
    visible: false,
    filter: ""
  },
  title: {
    title: t("coupon.title"),
    modelKey: "title",
    visible: true,
    filter: ""
  },
  type: {
    title: t("coupon.type"),
    modelKey: "type",
    visible: true,
    filter: ""
  },
  enabled: {
    title: t("coupon.enabled"),
    modelKey: "enabled",
    visible: true,
    filter: ""
  },
  research: {
    title: t("coupon.research"),
    modelKey: "research",
    visible: false,
    filter: ""
  },
  cohort: {
    title: t("coupon.cohort"),
    modelKey: "cohort",
    visible: false,
    filter: ""
  },
  maxUses: {
    title: t("coupon.max_uses"),
    modelKey: "maxUses",
    visible: true,
    filter: ""
  },
  usageCount: {
    title: t("coupon.usage_count"),
    modelKey: "usageCount",
    visible: true,
    filter: ""
  },
  amount: {
    title: t("coupon.amount"),
    modelKey: "amount",
    visible: false,
    filter: ""
  },
  expiresAt: {
    title: t("coupon.expires_at"),
    modelKey: "expiresAt",
    visible: true,
    filter: ""
  },
  createdAt: {
    title: t("common.created_at"),
    modelKey: "createdAt",
    visible: true,
    filter: ""
  },
};

const {
  columns,
  headers,
  page,
  perPage,
  sort,
  activeFilterCount,
  resetFilter,
  createUrlFilterParameters,
  onUpdatePerPage,
  onSort,
  onNext,
  onPrev,
  onPageChange,
  setupFooterData,
} = usePagination(fetchPage, {
  initialColumns: cols,
  initialSort: [{
    key: "createdAt",
    order: "asc"
  }]
});

onMounted(async () => {
  setupParams();
  await fetchPage();
});

const titleFilter: WritableComputedRef<string> = computed({
  get: (): string => columns.title.filter.toString(),
  set: (val: string | string[]) => {
    columns.title.filter = val.toString();
  }
});

watch([() => columns.title.filter, () => columns.type.filter, () => columns.enabled.filter, () => columns.usageCount.filter, used], () => {
  fetchPage();
});

async function fetchPage() {
  const params: CouponPaginationParams = {
    page: page.value,
    size: perPage.value,
    sortBy: sort.value[0].key,
    sortOrder: sort.value[0].order,

    type: columns.type.filter as string || undefined,
    filterCode: columns.title.filter as string || undefined,
    maxUses: columns.maxUses.filter as string || undefined,
    used: used.value as string || undefined,
  };

  try {
    store.networkBusy = true;
    paginatedData.value = (await yapi.admin.coupon.paginate(params)).data;
    await router.push({query: createUrlFilterParameters(params)});
    coupons.value = paginatedData.value.docs;
    paginationFooterData = setupFooterData(paginatedData.value);
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
  }
}

function setupParams() {
  page.value = route.query.page ? parseInt(route.query.page as string) : page.value;
  perPage.value = route.query.size ? parseInt(route.query.size as string) : perPage.value;
  sort.value[0].key = route.query.sortBy as string || sort.value[0].key;
  sort.value[0].order = route.query.sortOrder as "asc" | "desc" || sort.value[0].order;

  columns.type.filter = route.query.type as string || columns.type.filter;
  columns.title.filter = route.query.filterCode as string || columns.title.filter,
  columns.maxUses.filter = route.query.maxUses as string || columns.maxUses.filter,
  used.value = route.query.used as string || used.value;
}

const onClickRow = async (event: Event, row: { internalItem: { raw?: Coupon } }) => {
  try {
    if (row.internalItem && row.internalItem.raw) {
      const couponId = row.internalItem.raw._id;
      if (couponId) {
        await openDetailPage(couponId);
      }
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  }
};

const openDetailPage = async (id: string) => {
  await router.push({
    name: "couponDetail",
    params: {id}
  });
};

const couponTypes: VSelectOption[] = [
  {title: t("couponTypes.full"), value: "full"},
  {title: t("couponTypes.percent"), value: "percent"},
  {title: t("couponTypes.fixed"), value: "fixed"}
];
</script>
